import { he } from "./he";

/**
 * Runtime translation pass for page copy.
 *
 * The marketing pages hold their copy inline, so instead of threading t()
 * through every page we translate the rendered text nodes once Hebrew is
 * active. Lookups are keyed by the exact (trimmed) English source string, so
 * anything missing from the dictionary simply stays in English.
 */

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA"]);
const ATTRIBUTES = ["aria-label", "placeholder", "title", "alt", "aria-description"];

const lookup = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const translated = he[trimmed];
  if (translated === undefined || translated === trimmed) return null;
  return value.replace(trimmed, translated);
};

const translateTextNode = (node: Text) => {
  const parent = node.parentElement;
  if (!parent || SKIP_TAGS.has(parent.tagName)) return;
  if (parent.closest("[data-no-translate]")) return;

  const next = lookup(node.nodeValue ?? "");
  if (next !== null) node.nodeValue = next;
};

const translateAttributes = (element: Element) => {
  ATTRIBUTES.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (!value) return;
    const next = lookup(value);
    if (next) element.setAttribute(attribute, next);
  });
};

const translateTree = (root: Node) => {
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root as Text);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE) return;

  const element = root as Element;
  translateAttributes(element);
  element.querySelectorAll("*").forEach(translateAttributes);

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }
  nodes.forEach(translateTextNode);
};

/**
 * Translates everything currently rendered and keeps translating nodes that
 * React adds later (lazy routes, dialogs, toasts). Returns a cleanup function.
 */
export const startDomTranslation = (): (() => void) => {
  const root = document.getElementById("root");
  if (!root) return () => undefined;

  translateTree(root);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "characterData") {
        translateTextNode(mutation.target as Text);
        return;
      }
      mutation.addedNodes.forEach(translateTree);
    });
  });

  observer.observe(root, { childList: true, subtree: true, characterData: true });

  return () => observer.disconnect();
};
